import React from 'react';
import { 
  ArrowUpIcon, 
  ArrowDownIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';


export interface TableColumn<T = any> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (value: any, row: T) => React.ReactNode;
  className?: string;
}

export interface TableAction<T = any> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  variant?: 'primary' | 'secondary' | 'danger';
  show?: (row: T) => boolean;
  render?: (row: T) => boolean;
}

interface DataTableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  selectable?: boolean;
  selectedItems?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getRowId?: (row: T) => string;
  getRowProps?: (row: T) => Record<string, any>;
  actions?: TableAction<T>[];
  emptyMessage?: string;
  className?: string;
  onRowClick?: (row: T) => void;
}

function DataTable<T = any>({
  columns,
  data,
  loading = false,
  error = null,
  sortBy,
  sortOrder,
  onSort,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  getRowId = (row: any) => row.id,
  getRowProps = () => ({}),
  actions = [],
  emptyMessage = 'No data available',
  className = '',
  onRowClick,
}: DataTableProps<T>) {
  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    
    if (selectedItems.length === data.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(data.map(getRowId));
    }
  };

  const handleSelectRow = (rowId: string) => {
    if (!onSelectionChange) return;
    
    if (selectedItems.includes(rowId)) {
      onSelectionChange(selectedItems.filter(id => id !== rowId));
    } else {
      onSelectionChange([...selectedItems, rowId]);
    }
  };

  const handleRowClick = (row: T, e: React.MouseEvent) => {
    // Prevent triggering row click when clicking on action buttons or checkboxes
    const target = e.target as HTMLElement;
    if (target.closest('button, a, [role="button"], input[type="checkbox"]')) {
      return;
    }
    onRowClick?.(row);
  };

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? 
      <ArrowUpIcon className="w-4 h-4 ml-1" /> : 
      <ArrowDownIcon className="w-4 h-4 ml-1" />;
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : '';
    }, obj);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-soft overflow-hidden ${className}`}>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <EllipsisVerticalIcon className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg font-medium">{emptyMessage}</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" role="table">
            <thead className="bg-gray-50">
              <tr role="row">
                {selectable && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={selectedItems.length === data.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}
                    style={column.width ? { width: column.width } : undefined}
                    role="columnheader"
                  >
                    <button
                      type="button"
                      className={`whitespace-nowrap px-6 py-4 text-sm text-gray-500 ${column.className || ''}`}
                      onClick={() => column.sortable && onSort?.(column.key)}
                      disabled={!column.sortable}
                    >
                      <span>{column.header}</span>
                      {column.sortable && <SortIcon column={column.key} />}
                    </button>
                  </th>
                ))}
                {actions.length > 0 && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => {
                const rowId = getRowId(row);
                return (
                  <tr
                    key={rowId}
                    className={`${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    } ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${getRowProps(row).className || ''}`}
                    onClick={(e) => onRowClick && handleRowClick(row, e)}
                    {...Object.fromEntries(
                      Object.entries(getRowProps(row)).filter(([key]) => key !== 'className')
                    )}
                  >
                    {selectable && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={selectedItems.includes(rowId)}
                          onChange={() => handleSelectRow(rowId)}
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {column.render ? column.render(getNestedValue(row, column.key), row) : getNestedValue(row, column.key)}
                      </td>
                    ))}
                    {actions.length > 0 && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {actions
                            .filter((action) => (action.show ? action.show(row) : true))
                            .map((action) => (
                              <button
                                key={action.key}
                                onClick={() => action.onClick(row)}
                                className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                                  action.variant === 'danger'
                                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                    : action.variant === 'secondary'
                                    ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                    : 'text-white bg-primary-600 hover:bg-primary-700'
                                }`}
                              >
                                {action.icon && <span className="mr-2">{action.icon}</span>}
                                {action.label}
                              </button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataTable;
